import React, { useState, useEffect } from "react";
import { ThemeProvider } from "./contexts/ThemeContext";
import { STAADWorkspace3D } from "./components/workspace";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import MenuBar from "./components/menu/MenuBar";
import { CursorProvider } from "./components/menu/contexts/CursorContext";
import { useDocumentTitle } from "./hooks/useDocumentTitle";
import { useBeamTrussStore } from "@/hooks/useBeamTrussManagement";
import { useTrussStore } from "@/hooks/useTrussManagement";
import { useModelStore } from "@/store/useModelStore";
import { CursorModeIndicator } from "./components/ui/CursorModeIndicator";
import { SelectionOverlay, CapturePreviewModal } from "./components/capture";

type ViewType = "front" | "top" | "side" | "isometric";

const App: React.FC = () => {
  // Initialize keyboard shortcuts
  useKeyboardShortcuts();

  // State for current view - set to front view by default
  const [currentView, setCurrentView] = useState<ViewType>("front");

  useDocumentTitle();

  // Clear stale localStorage if no model data exists
  useEffect(() => {
    const initializeApp = () => {
      const { nodes, beams } = useModelStore.getState();
      const hasModelData =
        (nodes && nodes.length > 0) || (beams && beams.length > 0);

      if (!hasModelData) {
        // Clear predefined loads from localStorage
        localStorage.removeItem("predefined_loads");
        localStorage.removeItem("predefined_truss_presets");

        // Clear beam trusses from store
        const beamStore = useBeamTrussStore.getState() as any;
        if (beamStore.clearAllBeamTrusses) {
          beamStore.clearAllBeamTrusses();
        }

        // Clear node trusses from store
        const trussStore = useTrussStore.getState() as any;
        if (trussStore.clearAllTrusses) {
          trussStore.clearAllTrusses();
        }

        // Notify UI components to refresh
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent("predefined_loads_updated"));
          window.dispatchEvent(
            new CustomEvent("predefined_truss_presets_updated")
          );
        }, 100);
      } else {
      }
    };

    initializeApp();
  }, []);

  return (
    <ThemeProvider>
      <CursorProvider>
        <div className="h-screen w-screen overflow-hidden flex flex-col">
          {/* MenuBar at the top */}
          <MenuBar />

          {/* Main workspace */}
          <div
            className="h-screen w-screen overflow-hidden relative"
            data-workspace-canvas="true"
          >
            <STAADWorkspace3D
              currentView={currentView}
              onViewChange={setCurrentView}
            />

            <CursorModeIndicator />
          </div>

          {/* Capture Area Overlay (fullscreen, shown when in capture mode) */}
          <SelectionOverlay />
          
          {/* Capture Preview Modal (shown after capture complete) */}
          <CapturePreviewModal />
        </div>
      </CursorProvider>
    </ThemeProvider>
  );
};

export default App;
